import { useCallback, useEffect, useState } from 'react';
import SearchTab from './SearchTab';
import BasketTab from './BasketTab';
import ProfileTab from './ProfileTab';
import DealsTab from './DealsTab';
import JoinListDialog from './JoinListDialog';
import { useBasket } from '../utils/basketStore';
import FeedbackDialog from './FeedbackDialog';
import { useAuth } from '../utils/AuthContext';
import { RUN_SEARCH_EVENT } from '../utils/utils';

// טאב חדש = קובץ XxxTab.jsx + רשומה כאן + case ב-renderTab + פונקציות ב-API (service/ProductsService.js).
// requiresLogin (ההודעה בחלון הכניסה) — לחיצה בלי התחברות פותחת את חלון הכניסה, והטאב נפתח אחריה
const TABS = [
  { key: 'search', label: 'השוואת מחירים' },
  { key: 'deals', label: '🔥 מבצעים' },
  { key: 'basket', label: 'רשימת קניות', requiresLogin: 'רשימת הקניות למשתמשים רשומים — נא להתחבר' },
  { key: 'profile', label: 'הפרופיל שלי', requiresLogin: 'כדי לראות את הפרופיל צריך להתחבר' },
];

const s = {
  page:     { maxWidth: 1040, margin: '0 auto', padding: '24px 16px 48px' },
  header:   { marginBottom: 20 },
  topRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' },
  userBox:  { display: 'flex', alignItems: 'center', gap: 10, color: '#59636e', fontSize: 14 },
  userLink: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 4px', border: '1px solid transparent', borderRadius: 20, background: 'none', color: '#1f2328', fontSize: 14, fontWeight: 600 },
  userAvatar: { width: 28, height: 28, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#1a3f9c', color: '#fff', fontSize: 12, fontWeight: 700 },
  logout:   { padding: '6px 12px', border: '1px solid #d0d7de', borderRadius: 8, background: '#fff', color: '#1f2328', fontSize: 13 },
  login:    { padding: '8px 16px', border: 'none', borderRadius: 8, background: '#1a3f9c', color: '#fff', fontSize: 14, fontWeight: 600 },
  title:    { margin: 0, fontSize: 28, fontWeight: 700 },
  subtitle: { margin: '4px 0 0', color: '#59636e' },
  nav:      { display: 'flex', gap: 8, margin: '16px 0 0', borderBottom: '1px solid #e5e7eb' },
  navItem:  { padding: '8px 14px', background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#59636e' },
  navActive:{ color: '#1f2328', fontWeight: 600, borderBottomColor: '#1f2328' },
  count:    { display: 'inline-block', minWidth: 20, marginInlineStart: 6, padding: '0 6px', borderRadius: 10, background: '#1a3f9c', color: '#fff', fontSize: 12, lineHeight: '20px', textAlign: 'center' },
  footer:   { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 40, padding: '20px 16px', borderTop: '1px solid #e5e7eb', color: '#59636e', textAlign: 'center' },
  feedback: { padding: '8px 18px', border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c', fontWeight: 600 },
};

// האתר פתוח גם בלי התחברות; user / requireLogin / logout מ-AuthContext (ZolBakfarApp)
const MainPage = () => {
  const { user, requireLogin, logout } = useAuth();
  const basketItems = useBasket();
  const [activeTab, setActiveTab] = useState('search');
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // התנתקות / טוקן שפג בזמן שטאב שדורש התחברות פתוח — חוזרים לחיפוש
  useEffect(() => {
    if (!user && TABS.find((t) => t.key === activeTab)?.requiresLogin) setActiveTab('search');
  }, [user, activeTab]);
  // "חיפוש שוב" מעמוד הפרופיל — עוברים לחיפוש (SearchTab מריץ אותו) ולראש העמוד
  useEffect(() => {
    const onRun = () => { setActiveTab('search'); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    window.addEventListener(RUN_SEARCH_EVENT, onRun);
    return () => window.removeEventListener(RUN_SEARCH_EVENT, onRun);
  }, []);

  // קישור הזמנה לרשימה משותפת (ShareListDialog): <site>/?joinList=<code>. מורידים אותו מהכתובת (שלא יחזור ברענון),
  // ואחרי התחברות פותחים את JoinListDialog
  const [joinCode, setJoinCode] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('joinList');
    if (!code) return;
    params.delete('joinList');
    const rest = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    requireLogin('הוזמנת לרשימת קניות משותפת — נא להתחבר או להירשם כדי להצטרף', () => setJoinCode(code));
  }, [requireLogin]);
  const closeJoin = useCallback(() => setJoinCode(null), []);
  const onJoined = useCallback(() => { setJoinCode(null); setActiveTab('basket'); }, []);

  // useCallback: FeedbackDialog נרשם ל-Escape לפי onClose, ולא צריך להירשם מחדש בכל רינדור
  const closeFeedback = useCallback(() => setFeedbackOpen(false), []);

  // כל הטאבים נשארים טעונים (מוסתרים) — מעבר בין חיפוש לרשימת קניות לא מאבד את החיפוש
  const renderTab = (key) => {
    switch (key) {
      case 'deals': return <DealsTab active={activeTab === 'deals'} />;
      case 'basket': return <BasketTab active={activeTab === 'basket'} />;
      case 'profile': return <ProfileTab active={activeTab === 'profile'} />;
      case 'search':
      default:
        return <SearchTab />;
    }
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.topRow}>
          <div>
            <h1 style={s.title}>זול בכפר</h1>
            <p style={s.subtitle}>השוואת מחירים</p>
          </div>
          <div style={s.userBox}>
            {user ? (
              <>
                <button style={s.userLink} onClick={() => setActiveTab('profile')} title="לעמוד הפרופיל">
                  <span style={s.userAvatar} aria-hidden="true">{user.firstName?.[0]}{user.lastName?.[0]}</span>
                  שלום, {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                </button>
                <button style={s.logout} onClick={logout}>יציאה</button>
              </>
            ) : (
              <button style={s.login} onClick={() => requireLogin(null)}>כניסה / הרשמה</button>
            )}
          </div>
        </div>
        {TABS.length > 1 && (
          <nav style={s.nav}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                style={{ ...s.navItem, ...(activeTab === tab.key ? s.navActive : null) }}
                onClick={() => (tab.requiresLogin
                  ? requireLogin(tab.requiresLogin, () => setActiveTab(tab.key))
                  : setActiveTab(tab.key))}
              >
                {tab.label}
                {tab.key === 'basket' && basketItems.length > 0 && <span style={s.count}>{basketItems.length}</span>}
              </button>
            ))}
          </nav>
        )}
      </header>

      {TABS.map((tab) => (
        <div key={tab.key} hidden={activeTab !== tab.key}>{renderTab(tab.key)}</div>
      ))}

      <footer style={s.footer}>
        <span>אהבתם? יש לכם רעיון איך אפשר לשפר את האתר?</span>
        <button style={s.feedback} onClick={() => requireLogin('כדי לשלוח פידבק צריך להתחבר', () => setFeedbackOpen(true))}>שלחו לנו פידבק</button>
      </footer>

      {feedbackOpen && <FeedbackDialog onClose={closeFeedback} />}
      {joinCode && user && <JoinListDialog code={joinCode} onClose={closeJoin} onJoined={onJoined} />}
    </div>
  );
};

export default MainPage;
